import { Autocomplete, TextField, TextFieldVariants } from "@mui/material";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import { useState, useRef } from "react";
import useDebounce from "hooks/useDebounce";
import useGlobalContext from "hooks/globalData";
import {
  AdminListRequestOfBaseFilter,
  BaseFilter,
  Qualification,
} from "services/KnowfullyAdminClient";
import { formatQualification } from "helpers/FormatHelpers";

type QualificationOption = {
  id: string;
  label: string;
};

type SelectQualificationMultipleProps = {
  value: string[];
  onChange: (v: string[]) => void;
  variant?: TextFieldVariants;
  disabled?: boolean;
};

const SelectQualificationMultiple = ({
  onChange,
  value,
  variant,
  disabled,
}: SelectQualificationMultipleProps) => {
  const { catalogService } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm.trim(), 500);
  const labelCache = useRef(new Map<string, string>());

  const cacheQualifications = (qualifications?: Qualification[]) => {
    qualifications?.forEach((q) => {
      if (q.id) labelCache.current.set(q.id, formatQualification(q));
    });
  };

  const searchRequest = AdminListRequestOfBaseFilter.fromJS({
    pageNumber: 0,
    pageSize: 20,
    sortField: "value",
    parameters: BaseFilter.fromJS({
      searchText: debouncedSearch || undefined,
    }),
  });

  const { data: searchData, isLoading: isSearching } = useQuery(
    ["catalogService.getQualifications", searchRequest],
    () => catalogService.getQualifications(searchRequest),
    {
      keepPreviousData: true,
      onSuccess: (res) => cacheQualifications(res?.data),
    },
  );

  const unresolvedIds = value.filter((id) => !labelCache.current.has(id));

  const resolveRequest = AdminListRequestOfBaseFilter.fromJS({
    pageNumber: 0,
    pageSize: unresolvedIds.length,
    parameters: BaseFilter.fromJS({
      includedIds: unresolvedIds,
    }),
    sortField: "value",
  });

  const { data: resolveData, isLoading: isResolving } = useQuery(
    ["catalogService.getQualifications", resolveRequest],
    () => catalogService.getQualifications(resolveRequest),
    {
      enabled: unresolvedIds.length > 0,
      keepPreviousData: true,
      onSuccess: (res) => cacheQualifications(res?.data),
    },
  );

  cacheQualifications(searchData?.data);
  cacheQualifications(resolveData?.data);

  const getLabel = (id: string) => labelCache.current.get(id) ?? id;

  const selectedOptions: QualificationOption[] = value.map((id) => ({
    id,
    label: getLabel(id),
  }));

  const options = (() => {
    const seen = new Set(value);
    const result = [...selectedOptions];

    searchData?.data?.forEach((q) => {
      if (q.id && !seen.has(q.id)) {
        result.push({ id: q.id, label: formatQualification(q) });
        seen.add(q.id);
      }
    });

    return result;
  })();

  return (
    <Autocomplete<QualificationOption, true>
      multiple
      options={options}
      value={selectedOptions}
      loading={isSearching || isResolving}
      disabled={disabled}
      filterSelectedOptions
      inputValue={searchTerm}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      ChipProps={{ size: "small" }}
      onInputChange={(_, inputValue, reason) => {
        if (reason !== "reset") setSearchTerm(inputValue);
      }}
      onChange={(_, newValue) => {
        onChange(newValue.map((o) => o.id));
        setSearchTerm("");
      }}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          label={t({ id: "accreditationBodyLabel" })}
        />
      )}
    />
  );
};

export default SelectQualificationMultiple;
